import { LuLogIn } from 'react-icons/lu';
import { useEffect, useState } from 'react';
import { Switch } from '@/components/ui/switch';
import type { AdminApiOrganizationUser } from '@/types/serializers';
import useUpdateAdminOrganizationUser from '../../api/useUpdateAdminOrganizationUser';
import usePostAdminImpersonation from '../../api/usePostAdminImpersonation';
import { AdminButton } from '../AdminButton';
import AdminErrorMessage from '../atoms/AdminErrorMessage';
import AdminModal from '../molecules/AdminModal';

type AdminOrganizationStaffModalProps = {
  open: boolean;
  onClose: () => void;
  staff: AdminApiOrganizationUser | null;
  organizationId: number;
};

export default function AdminOrganizationStaffModal({
  open,
  onClose,
  staff,
  organizationId,
}: AdminOrganizationStaffModalProps) {
  const { mutateAsync: updateStaff, isPending: isUpdating } = useUpdateAdminOrganizationUser();
  const { mutateAsync: postImpersonation, isPending: isImpersonating } =
    usePostAdminImpersonation();
  const [error, setError] = useState<string | null>(null);
  // 親から渡される staff prop は selectedStaff state スナップショットなので、
  // PATCH 後の最新値を即時反映するためモーダル内で is_support をローカル管理する
  const [isSupport, setIsSupport] = useState(false);

  useEffect(() => {
    if (staff) {
      setIsSupport(staff.is_support);
      setError(null);
    }
  }, [staff?.id, staff?.is_support]);

  if (!staff) return null;

  const handleToggleSupport = async (checked: boolean) => {
    setIsSupport(checked);
    setError(null);
    try {
      await updateStaff({
        id: staff.id,
        organizationId,
        is_support: checked,
      });
    } catch (err) {
      setIsSupport(!checked);
      setError(err instanceof Error ? err.message : 'サポートアカウントの切り替えに失敗しました');
    }
  };

  const handleImpersonate = async () => {
    const confirmed = window.confirm(`${staff.name}（サポートアカウント）としてログインしますか？`);
    if (!confirmed) return;
    setError(null);
    try {
      const result = await postImpersonation({ organization_user_id: staff.id });
      window.location.href = result.redirect_url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'サポートアカウントログインに失敗しました');
    }
  };

  return (
    <AdminModal
      open={open}
      onClose={onClose}
      title={staff.name}
      maxWidth="max-w-[480px]"
      footer={
        <div className="flex items-center justify-end gap-4">
          <AdminButton label="閉じる" variant="secondary" onClick={onClose} />
        </div>
      }
    >
      <div className="flex flex-col gap-6 py-2">
        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-col gap-1">
            <span className="text-sm font-bold text-[#333]">サポートアカウント</span>
            <span className="text-xs text-[#777]">
              社内サポート用のアカウントとしてマークします。ONにすると代理ログインの対象になります。
            </span>
          </div>
          <Switch
            checked={isSupport}
            disabled={isUpdating}
            onCheckedChange={handleToggleSupport}
            aria-label={`${staff.name} をサポートアカウントに切り替える`}
          />
        </div>
        {isSupport && staff.can_impersonate && (
          <div className="flex justify-center">
            <AdminButton
              label={isImpersonating ? 'ログイン中...' : 'サポートでログイン'}
              icon={<LuLogIn className="size-4" aria-hidden="true" />}
              disabled={isImpersonating}
              onClick={handleImpersonate}
            />
          </div>
        )}
        <AdminErrorMessage message={error} />
      </div>
    </AdminModal>
  );
}
